import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, XCircle, ChevronDown } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import type { ApiResponse } from "@/types/dataQuality";

interface EnhancedDashboardProps {
  stats: {
    timeSeriesData: Array<{
      date: string;
      success: number;
      warning: number;
      failure: number;
    }>;
    totalTables: number;
    totalFields: number;
  };
  apiResponse?: ApiResponse;
}

interface ErrorItem {
  identificador: string;
  errors: Array<{
    fieldLabel: string;
    message: string;
    valor: any;
  }>;
}

export function EnhancedDataQualityDashboard({ stats, apiResponse }: EnhancedDashboardProps) {
  const [isErrorListOpen, setIsErrorListOpen] = useState(true);
  
  // Get validation statistics from API response
  const summary = apiResponse?.summary || {
    total_groups: 0,
    errors: 0,
    warnings: 0,
    valids: 0
  };

  const totalChecks = summary.valids + summary.errors + summary.warnings;

  const validationStats = {
    total_checks: totalChecks,
    passed_checks: summary.valids,
    failed_checks: summary.errors,
    warnings_checks: summary.warnings,
  };

  // Collect all error items from hierarchy
  const errorItems: ErrorItem[] = [];
  
  if (apiResponse?.hierarchy) {
    const collectErrors = (nodes: any[]) => {
      nodes.forEach(node => {
        if (node.items) {
          node.items.forEach((item: any) => {
            if (item.status === 'error' && item.errors) {
              errorItems.push({
                identificador: item.identificador,
                errors: item.errors
              });
            }
          });
        }
        if (node.children) {
          collectErrors(node.children);
        }
      });
    };
    
    collectErrors(apiResponse.hierarchy);
  }

  return (
    <div className="mb-6">
      {/* Error Focus Card */}
      {validationStats.failed_checks > 0 && (
        <Card className="border-destructive bg-destructive/5 mb-4">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-destructive mb-1">
                  {validationStats.failed_checks} Erro{validationStats.failed_checks !== 1 ? 's' : ''} Detectado{validationStats.failed_checks !== 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Revise os itens com falha abaixo para corrigir os problemas
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-destructive">{validationStats.failed_checks}</p>
                <p className="text-sm text-muted-foreground">
                  de {validationStats.total_checks} validações
                </p>
              </div>
            </div>

            {/* Error List - Collapsible */}
            <Collapsible open={isErrorListOpen} onOpenChange={setIsErrorListOpen}>
              <div className="mt-4 pt-4 border-t border-destructive/20">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between text-sm font-medium hover:bg-destructive/10"
                  >
                    <span>
                      {isErrorListOpen ? 'Recolher' : 'Expandir'} lista de erros ({errorItems.length})
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isErrorListOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </Button>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="space-y-3 mt-3">
                    {errorItems.map((item, idx) => (
                      <Alert key={idx} variant="destructive" className="bg-destructive/10">
                        <XCircle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="font-medium mb-2">{item.identificador}</div>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {item.errors.map((error, errorIdx) => (
                              <li key={errorIdx}>
                                <strong>{error.fieldLabel}:</strong> {error.message}
                                {error.valor !== undefined && error.valor !== null && (
                                  <span className="text-muted-foreground ml-1">
                                    (Valor: {typeof error.valor === 'object' ? JSON.stringify(error.valor) : error.valor})
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          </CardContent>
        </Card>
      )}
    </div>
  );
}